// Parses the canonical Yourchecka policy text into the same Block[] shape
// the help app's TermsContent already understands. Single source of truth lives
// in `data/podus-checker-policy.txt` — update that file and the help page picks
// the change up automatically.

import rawPolicy from '../../data/podus-checker-policy.txt?raw';

export type Block =
    | { type: 'heading'; text: string }
    | { type: 'subheading'; text: string }
    | { type: 'body'; text: string }
    | { type: 'bullet'; text: string }
    | { type: 'indent-bullet'; text: string };

export interface ParsedPolicy {
    effectiveDate: string;
    privacy: Block[];
    terms: Block[];
}

// Decode the HTML entities the source file ships with (`&amp;`, `&quot;`, …).
// Keeping the file as a raw export lets non-engineers edit the policy without
// touching code, so it's worth absorbing the entities here.
function decodeEntities(input: string): string {
    return input
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&#x27;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&nbsp;/g, ' ');
}

const isAllUpper = (s: string) => s === s.toUpperCase() && /[A-Z]/.test(s);

/**
 * Title-Case section heading, e.g. "1. Who we are" or "13. If you are the
 * subject of a check, not our Customer".
 *
 * Deliberately conservative, because a false positive turns a paragraph of the
 * policy into a heading. A heading is short and has no terminal full stop after
 * the number — prose that happens to start "1. " almost always runs on and ends
 * in one.
 */
const isLikelyHeading = (s: string): boolean => {
    const afterNumber = s.replace(/^\d+\.\s+/, '');
    if (afterNumber.length > 80) return false;
    if (/[.!?]$/.test(afterNumber)) return false;
    return /^[A-Z]/.test(afterNumber);
};

function parse(raw: string): ParsedPolicy {
    const privacy: Block[] = [];
    const terms: Block[] = [];
    let effectiveDate = '';

    // `current` swaps to either the privacy or terms array based on which PART
    // we've crossed; lines before the first PART (title block + TOC) are
    // metadata and don't end up in either bucket.
    let current: Block[] | null = null;
    let bodyBuffer: string[] = [];
    let inToc = false;

    const flushBody = () => {
        if (!current || bodyBuffer.length === 0) {
            bodyBuffer = [];
            return;
        }
        const text = decodeEntities(bodyBuffer.join(' ').replace(/\s+/g, ' ').trim());
        if (text) current.push({ type: 'body', text });
        bodyBuffer = [];
    };

    const lines = raw.replace(/\r\n/g, '\n').split('\n');

    for (const rawLine of lines) {
        const line = rawLine.trim();

        if (!line) {
            flushBody();
            continue;
        }

        // The source has used both "Effective Date:" and "Last reviewed:" across
        // revisions; accept either so a wording change in the text file doesn't
        // silently blank the date on the page.
        if (/^(effective date|last reviewed)/i.test(line) && !effectiveDate) {
            effectiveDate = decodeEntities(line.replace(/^(effective date|last reviewed)[:\s]*/i, '').trim());
            continue;
        }

        if (line === 'TABLE OF CONTENTS') {
            inToc = true;
            continue;
        }

        // The TOC is a flat list of bullet items — skip everything inside it
        // until we hit the first PART heading, which kicks us into the real
        // content of the document.
        if (/^PART\s+I\b/i.test(line)) {
            flushBody();
            inToc = false;
            current = privacy;
            current.push({ type: 'heading', text: decodeEntities(line) });
            continue;
        }
        if (/^PART\s+II\b/i.test(line)) {
            flushBody();
            inToc = false;
            current = terms;
            current.push({ type: 'heading', text: decodeEntities(line) });
            continue;
        }

        if (inToc) continue;
        if (!current) continue;

        // Bullet line.
        if (line.startsWith('•')) {
            flushBody();
            current.push({ type: 'bullet', text: decodeEntities(line.slice(1).trim()) });
            continue;
        }

        // Numbered subsection (e.g. "4.1 Identity and Personal Information").
        if (/^\d+\.\d+\s+\S/.test(line)) {
            flushBody();
            current.push({ type: 'subheading', text: decodeEntities(line) });
            continue;
        }

        // Numbered top-level section. The privacy half is Title Case
        // ("1. Who we are") and the terms half is all-caps
        // ("1. INTRODUCTION AND ACCEPTANCE"), so casing can't be the test.
        // Instead: a heading is short and carries no sentence-ending full stop
        // beyond its own number, which distinguishes it from a numbered
        // paragraph of prose.
        if (/^\d+\.\s+\S/.test(line) && (isAllUpper(line) || isLikelyHeading(line))) {
            flushBody();
            current.push({ type: 'heading', text: decodeEntities(line) });
            continue;
        }

        // Continuation logic: most paragraphs in the source file wrap mid-line
        // without a blank separator. Treat the current line as a new paragraph
        // if (a) the previous line ended a sentence AND (b) this line starts
        // with a capital letter, a quote, or a section reference. Otherwise
        // append to the previous body line.
        if (bodyBuffer.length > 0) {
            const prev = bodyBuffer[bodyBuffer.length - 1] || '';
            const prevEndsSentence = /[.!?:]["')\]]?$/.test(prev);
            const lineStartsNewIdea = /^("|'|[A-Z]|\d+\.\d+|\d+\.\s)/.test(line);
            if (prevEndsSentence && lineStartsNewIdea) {
                flushBody();
            }
        }
        bodyBuffer.push(line);
    }
    flushBody();

    return { effectiveDate, privacy, terms };
}

const parsed = parse(rawPolicy);

export const podusCheckerEffectiveDate = parsed.effectiveDate;
export const podusCheckerPrivacy: Block[] = parsed.privacy;
export const podusCheckerTerms: Block[] = parsed.terms;
